import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import CrmLayout from "@/components/feature/CrmLayout";
import { fetchApi } from "@/services/apiService";
import { useToast } from "@/components/base/Toast";

export interface ActivityItem {
  id: string | number;
  type: string;
  agent: string;
  agentInitials: string;
  action: string;
  leadName: string;
  leadId?: string | number | null;
  detail: string;
  time: string;
  timeBlock: string;
  timeSort: string;
  timestamp?: string;
  metadata?: any;
}

export type ActivityTypeFilter =
  | "All Activities"
  | "Calls"
  | "Notes"
  | "Stage Changes"
  | "Lead Created"
  | "WhatsApp"
  | "Assignments"
  | "Score Changes"
  | "Quotations"
  | "Projects"
  | "Units"
  | "Tasks"
  | "Campaigns";

const activityTypeFilters: ActivityTypeFilter[] = [
  "All Activities",
  "Calls",
  "Notes",
  "Stage Changes",
  "Lead Created",
  "WhatsApp",
  "Assignments",
  "Score Changes",
  "Quotations",
  "Projects",
  "Units",
  "Tasks",
  "Campaigns",
];

const timeBlocks = ["MORNING", "AFTERNOON", "EVENING"] as const;

export function getActivityIcon(type: string): string {
  const map: Record<string, string> = {
    call: "ri-phone-line",
    call_scheduled: "ri-phone-line",
    call_deleted: "ri-phone-line",
    note: "ri-sticky-note-line",
    stage_change: "ri-arrow-right-circle-line",
    lead_created: "ri-user-add-line",
    lead_updated: "ri-user-settings-line",
    lead_deleted: "ri-user-unfollow-line",
    whatsapp: "ri-whatsapp-line",
    assignment: "ri-user-shared-line",
    reassigned: "ri-user-shared-line",
    score_change: "ri-line-chart-line",
    quotation: "ri-file-text-line",
    quotation_updated: "ri-file-edit-line",
    quotation_deleted: "ri-file-close-line",
    project: "ri-building-line",
    project_updated: "ri-building-line",
    project_deleted: "ri-building-line",
    unit: "ri-layout-grid-line",
    unit_updated: "ri-layout-grid-line",
    unit_booked: "ri-checkbox-circle-line",
    unit_deleted: "ri-layout-grid-line",
    task: "ri-checkbox-circle-line",
    task_completed: "ri-checkbox-circle-line",
    task_deleted: "ri-checkbox-circle-line",
    meeting: "ri-calendar-event-line",
    campaign: "ri-megaphone-line",
    campaign_launched: "ri-rocket-line",
    campaign_deleted: "ri-megaphone-line",
  };
  return map[(type || "").toLowerCase()] || "ri-pulse-line";
}

export function getActivityIconBg(type: string): string {
  const map: Record<string, string> = {
    call: "bg-green-50 text-green-600",
    call_scheduled: "bg-green-50 text-green-600",
    call_deleted: "bg-red-50 text-red-600",
    note: "bg-amber-50 text-amber-600",
    stage_change: "bg-blue-50 text-blue-600",
    lead_created: "bg-purple-50 text-purple-600",
    lead_updated: "bg-indigo-50 text-indigo-600",
    lead_deleted: "bg-red-50 text-red-600",
    whatsapp: "bg-emerald-50 text-emerald-500",
    assignment: "bg-indigo-50 text-indigo-600",
    reassigned: "bg-indigo-50 text-indigo-600",
    score_change: "bg-pink-50 text-pink-600",
    quotation: "bg-teal-50 text-teal-600",
    quotation_updated: "bg-teal-50 text-teal-600",
    quotation_deleted: "bg-red-50 text-red-600",
    project: "bg-violet-50 text-violet-600",
    project_updated: "bg-violet-50 text-violet-600",
    project_deleted: "bg-red-50 text-red-600",
    unit: "bg-orange-50 text-orange-600",
    unit_updated: "bg-orange-50 text-orange-600",
    unit_booked: "bg-emerald-50 text-emerald-600",
    unit_deleted: "bg-red-50 text-red-600",
    task: "bg-cyan-50 text-cyan-600",
    task_completed: "bg-green-50 text-green-600",
    task_deleted: "bg-red-50 text-red-600",
    meeting: "bg-cyan-50 text-cyan-600",
    campaign: "bg-amber-50 text-amber-600",
    campaign_launched: "bg-emerald-50 text-emerald-600",
    campaign_deleted: "bg-red-50 text-red-600",
  };
  return map[(type || "").toLowerCase()] || "bg-gray-50 text-gray-600";
}

export function formatTimeFromDate(timestampStr?: string, fallbackTime?: string): string {
  if (timestampStr) {
    try {
      const d = new Date(timestampStr);
      if (!isNaN(d.getTime())) {
        return d.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      }
    } catch {}
  }
  return fallbackTime || "Just now";
}

export default function ActivityFeedPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [selectedAgent, setSelectedAgent] = useState("Everyone");
  const [typeFilter, setTypeFilter] = useState<ActivityTypeFilter>("All Activities");
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Dynamic Data State
  const [allActivities, setAllActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [newActivityToast, setNewActivityToast] = useState<{ count: number; visible: boolean }>({
    count: 0,
    visible: false,
  });
  const [newItemIds, setNewItemIds] = useState<Set<string | number>>(new Set());

  // Dynamic Agents and Leads
  const [agentsList, setAgentsList] = useState<{ name: string; initials: string }[]>([
    { name: "Everyone", initials: "ALL" },
  ]);
  const [leadsList, setLeadsList] = useState<{ id: string | number; name: string; company?: string }[]>([]);

  // Log Activity Modal State
  const [showLogModal, setShowLogModal] = useState(false);
  const [logForm, setLogForm] = useState({
    leadId: "",
    leadName: "",
    type: "Call",
    agent: "",
    detail: "",
    date: new Date().toISOString().split("T")[0],
    time: new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false }),
  });
  const [leadSearchText, setLeadSearchText] = useState("");
  const [leadDropdownOpen, setLeadDropdownOpen] = useState(false);
  const [submittingLog, setSubmittingLog] = useState(false);

  const feedRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isAtTopRef = useRef(true);

  // Helper to format date for API: YYYY-MM-DD
  const formatDateForApi = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Fetch dynamic agents from company-user endpoint
  useEffect(() => {
    fetchApi<any>("/auth/company-user/all", undefined, [])
      .then((res) => {
        const userList = Array.isArray(res)
          ? res
          : Array.isArray(res?.items)
            ? res.items
            : Array.isArray(res?.data?.items)
              ? res.data.items
              : Array.isArray(res?.data)
                ? res.data
                : [];

        const distinctAgents: { name: string; initials: string }[] = [{ name: "Everyone", initials: "ALL" }];
        userList.forEach((u: any) => {
          const name = u.name || u.fullName || u.username;
          if (name && !distinctAgents.some((a) => a.name === name)) {
            const initials = name
              .split(" ")
              .filter(Boolean)
              .map((n: string) => n[0])
              .join("")
              .toUpperCase()
              .slice(0, 3);
            distinctAgents.push({ name, initials });
          }
        });

        if (distinctAgents.length > 1) {
          setAgentsList(distinctAgents);
          if (!logForm.agent && distinctAgents[1]) {
            setLogForm((prev) => ({ ...prev, agent: distinctAgents[1].name }));
          }
        }
      })
      .catch(() => {});
  }, []);

  // Fetch dynamic leads list for interlinking & logging modal
  useEffect(() => {
    fetchApi<any>("/leads?limit=200", undefined, [])
      .then((res) => {
        const items = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res?.items)
              ? res.items
              : [];
        setLeadsList(
          items.map((l: any) => ({
            id: l.id,
            name: l.name || "Unnamed Lead",
            company: l.company || "",
          }))
        );
      })
      .catch(() => {});
  }, []);

  // Fetch activities from backend for the selected date
  const loadActivities = useCallback(
    async (showLoadingSpinner = false) => {
      if (showLoadingSpinner) setLoading(true);

      const dateQuery = formatDateForApi(currentDate);
      try {
        const res = await fetchApi<any>(`/activities?date=${dateQuery}`, undefined, []);
        const validList: ActivityItem[] = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];

        // Check for new items if not initial load
        setAllActivities((prev) => {
          if (showLoadingSpinner) return validList;

          const prevIds = new Set(prev.map((a) => String(a.id)));
          const incomingNew = validList.filter((a) => !prevIds.has(String(a.id)));

          if (incomingNew.length > 0) {
            const newIds = new Set(incomingNew.map((a) => a.id));
            setNewItemIds(newIds);

            if (isAtTopRef.current) {
              setTimeout(() => setNewItemIds(new Set()), 2500);
              return validList;
            } else {
              setNewActivityToast({ count: incomingNew.length, visible: true });
              return prev;
            }
          }
          return validList;
        });

        // Merge any newly discovered agents into agent list
        if (validList.length > 0) {
          setAgentsList((prev) => {
            const existingNames = new Set(prev.map((a) => a.name));
            const updated = [...prev];
            validList.forEach((item) => {
              if (item.agent && !existingNames.has(item.agent)) {
                existingNames.add(item.agent);
                updated.push({
                  name: item.agent,
                  initials: item.agentInitials || item.agent.slice(0, 2).toUpperCase(),
                });
              }
            });
            return updated;
          });
        }
      } catch (err) {
        console.error("Error loading activities:", err);
      } finally {
        setLoading(false);
      }
    },
    [currentDate]
  );

  // Initial load whenever date changes
  useEffect(() => {
    loadActivities(true);
  }, [loadActivities]);

  // Scroll listener to track if user is at top of feed
  useEffect(() => {
    const feedEl = feedRef.current;
    if (!feedEl) return;
    const handler = () => {
      isAtTopRef.current = feedEl.scrollTop < 100;
    };
    feedEl.addEventListener("scroll", handler, { passive: true });
    return () => feedEl.removeEventListener("scroll", handler);
  }, []);

  // Polling logic - query backend every 15s for live real-time activities
  const startPolling = useCallback(() => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(() => {
      loadActivities(false);
    }, 15000);
  }, [loadActivities]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Tab visibility handling for polling
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === "visible") {
        startPolling();
      } else {
        stopPolling();
      }
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [startPolling, stopPolling]);

  useEffect(() => {
    startPolling();
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  const handleLoadNew = () => {
    setNewActivityToast({ count: 0, visible: false });
    loadActivities(true);
    if (feedRef.current) {
      feedRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Filter activities
  const filteredActivities = useMemo(() => {
    let result = [...allActivities];

    if (selectedAgent !== "Everyone") {
      result = result.filter((a) => a.agent.toLowerCase() === selectedAgent.toLowerCase());
    }

    if (typeFilter !== "All Activities") {
      const typeMap: Record<ActivityTypeFilter, string | null> = {
        "All Activities": null,
        Calls: "call",
        Notes: "note",
        "Stage Changes": "stage_change",
        "Lead Created": "lead",
        WhatsApp: "whatsapp",
        Assignments: "assign",
        "Score Changes": "score",
        Quotations: "quotation",
        Projects: "project",
        Units: "unit",
        Tasks: "task",
        Campaigns: "campaign",
      };
      const mappedType = typeMap[typeFilter];
      if (mappedType) {
        result = result.filter(
          (a) => a.type.toLowerCase() === mappedType || a.type.toLowerCase().includes(mappedType)
        );
      }
    }

    // Sort descending (most recent first)
    result.sort((a, b) => {
      const timeA = new Date(a.timestamp || a.timeSort || 0).getTime();
      const timeB = new Date(b.timestamp || b.timeSort || 0).getTime();
      return timeB - timeA;
    });

    return result;
  }, [allActivities, selectedAgent, typeFilter]);

  // Group activities into MORNING, AFTERNOON, EVENING based on local time
  const groupedActivities = useMemo(() => {
    const groups: Record<string, ActivityItem[]> = {
      MORNING: [],
      AFTERNOON: [],
      EVENING: [],
    };

    filteredActivities.forEach((act) => {
      let block = "MORNING";
      if (act.timestamp) {
        const h = new Date(act.timestamp).getHours();
        if (h < 12) block = "MORNING";
        else if (h < 17) block = "AFTERNOON";
        else block = "EVENING";
      } else if (act.timeBlock) {
        const raw = act.timeBlock.toUpperCase();
        if (raw === "MORNING" || raw === "AFTERNOON" || raw === "EVENING") {
          block = raw;
        }
      }
      groups[block].push(act);
    });

    return groups;
  }, [filteredActivities]);

  // Dynamic type counts for badge display
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = { "All Activities": allActivities.length };
    allActivities.forEach((a) => {
      const t = a.type.toLowerCase();
      if (t.includes("call")) counts["Calls"] = (counts["Calls"] || 0) + 1;
      else if (t.includes("note")) counts["Notes"] = (counts["Notes"] || 0) + 1;
      else if (t.includes("stage")) counts["Stage Changes"] = (counts["Stage Changes"] || 0) + 1;
      else if (t.includes("lead")) counts["Lead Created"] = (counts["Lead Created"] || 0) + 1;
      else if (t.includes("whatsapp")) counts["WhatsApp"] = (counts["WhatsApp"] || 0) + 1;
      else if (t.includes("assign")) counts["Assignments"] = (counts["Assignments"] || 0) + 1;
      else if (t.includes("score")) counts["Score Changes"] = (counts["Score Changes"] || 0) + 1;
      else if (t.includes("quote") || t.includes("quotation")) counts["Quotations"] = (counts["Quotations"] || 0) + 1;
      else if (t.includes("project")) counts["Projects"] = (counts["Projects"] || 0) + 1;
      else if (t.includes("unit")) counts["Units"] = (counts["Units"] || 0) + 1;
      else if (t.includes("task")) counts["Tasks"] = (counts["Tasks"] || 0) + 1;
      else if (t.includes("campaign")) counts["Campaigns"] = (counts["Campaigns"] || 0) + 1;
    });
    return counts;
  }, [allActivities]);

  // Date Labels
  const dateLabel = useMemo(() => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (currentDate.toDateString() === today.toDateString()) return "Today";
    if (currentDate.toDateString() === yesterday.toDateString()) return "Yesterday";
    return currentDate.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  }, [currentDate]);

  const handlePrevDay = () => {
    const prev = new Date(currentDate);
    prev.setDate(prev.getDate() - 1);
    setCurrentDate(prev);
  };

  const handleNextDay = () => {
    const next = new Date(currentDate);
    const today = new Date();
    if (next.toDateString() === today.toDateString()) return;
    next.setDate(next.getDate() + 1);
    setCurrentDate(next);
  };

  const isToday = currentDate.toDateString() === new Date().toDateString();

  // Navigation / Interlinking helpers
  const handleLeadClick = (activity: ActivityItem) => {
    if (activity.leadId) {
      navigate(`/leads/${activity.leadId}`);
      return;
    }
    const matched = leadsList.find(
      (l) => l.name.toLowerCase().trim() === activity.leadName.toLowerCase().trim()
    );
    if (matched) {
      navigate(`/leads/${matched.id}`);
      return;
    }
    navigate(`/leads?search=${encodeURIComponent(activity.leadName)}`);
  };

  const handleActionDetailClick = (activity: ActivityItem) => {
    const t = activity.type.toLowerCase();
    if (t.includes("call")) {
      navigate("/calls");
    } else if (t.includes("whatsapp")) {
      navigate("/whatsapp");
    } else if (t.includes("quote") || t.includes("quotation")) {
      navigate("/quotations");
    } else if (t.includes("project") || t.includes("unit")) {
      navigate("/projects");
    } else if (t.includes("campaign")) {
      navigate("/campaigns");
    } else {
      handleLeadClick(activity);
    }
  };

  // Submit new activity from modal
  const handleSaveActivityModal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!logForm.leadName.trim()) {
      showToast("Please select or enter a contact/lead name", "error");
      return;
    }
    if (!logForm.detail.trim()) {
      showToast("Please enter activity details", "error");
      return;
    }

    setSubmittingLog(true);
    const ts = new Date(`${logForm.date}T${logForm.time || "12:00"}`);

    const payload = {
      type: logForm.type.toLowerCase().replace(/\s+/g, "_"),
      title: `${logForm.type} logged`,
      description: logForm.detail.trim(),
      user_name: logForm.agent || "System",
      lead_name: logForm.leadName.trim(),
      lead_id: logForm.leadId || null,
      detail: logForm.detail.trim(),
      timestamp: ts.toISOString(),
    };

    try {
      const created = await fetchApi<ActivityItem>("/activities", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (logForm.leadId) {
        await fetchApi(`/leads/${logForm.leadId}/activities`, {
          method: "POST",
          body: JSON.stringify({
            type: logForm.type,
            description: logForm.detail.trim(),
            performedBy: logForm.agent || "System",
          }),
        }).catch(() => {});
      }

      showToast("Activity recorded successfully", "success");
      setShowLogModal(false);
      setLogForm((prev) => ({ ...prev, leadId: "", leadName: "", detail: "" }));
      setLeadSearchText("");

      if (created) {
        setAllActivities((prev) => [created, ...prev]);
        setNewItemIds(new Set([created.id]));
        setTimeout(() => setNewItemIds(new Set()), 2500);
      } else {
        loadActivities(true);
      }
    } catch (err: any) {
      showToast(err.message || "Failed to record activity", "error");
    } finally {
      setSubmittingLog(false);
    }
  };

  const filteredLeadsForModal = useMemo(() => {
    if (!leadSearchText.trim()) return leadsList.slice(0, 10);
    return leadsList
      .filter(
        (l) =>
          l.name.toLowerCase().includes(leadSearchText.toLowerCase()) ||
          (l.company && l.company.toLowerCase().includes(leadSearchText.toLowerCase()))
      )
      .slice(0, 10);
  }, [leadsList, leadSearchText]);

  return (
    <CrmLayout title="Activity Feed">
      <div className="p-4 md:p-6 space-y-4 max-w-[1400px] mx-auto">
        {/* Header & Date Picker */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Activity Feed</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Everything your team did — real time view
            </p>
          </div>

          {/* Date Navigator */}
          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-xl p-1 shadow-sm self-start sm:self-auto">
            <button
              onClick={handlePrevDay}
              title="Previous Day"
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>

            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="px-3 py-1 text-sm font-semibold text-gray-800 hover:text-indigo-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {dateLabel}
            </button>

            <button
              onClick={handleNextDay}
              disabled={isToday}
              title={isToday ? "Cannot navigate into the future" : "Next Day"}
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                isToday
                  ? "text-gray-300 cursor-not-allowed"
                  : "hover:bg-gray-100 text-gray-500 hover:text-gray-700 cursor-pointer"
              }`}
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>

            {/* Date picker popover */}
            {showDatePicker && (
              <div className="absolute right-6 top-32 z-40 bg-white border border-gray-200 rounded-xl shadow-xl p-3">
                <input
                  type="date"
                  value={formatDateForApi(currentDate)}
                  max={formatDateForApi(new Date())}
                  onChange={(e) => {
                    if (e.target.value) {
                      setCurrentDate(new Date(`${e.target.value}T12:00:00`));
                      setShowDatePicker(false);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-indigo-500"
                />
              </div>
            )}
          </div>
        </div>

        {/* Filter Pills & Type Dropdown */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
          {/* Agent Filter Pills */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1.5 no-scrollbar max-w-full">
            {agentsList.map((agent) => {
              const isSelected = selectedAgent === agent.name;
              return (
                <button
                  key={agent.name}
                  onClick={() => setSelectedAgent(agent.name)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap flex-shrink-0 cursor-pointer flex items-center gap-1.5 ${
                    isSelected
                      ? "bg-indigo-600 text-white shadow-sm"
                      : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {agent.name !== "Everyone" && (
                    <span
                      className={`text-[10px] font-bold px-1 rounded ${
                        isSelected ? "bg-white/20 text-white" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {agent.initials}
                    </span>
                  )}
                  <span>{agent.name}</span>
                </button>
              );
            })}
          </div>

          {/* Activity Type Dropdown */}
          <div className="relative flex-shrink-0 self-end sm:self-auto">
            <button
              onClick={() => setTypeDropdownOpen(!typeDropdownOpen)}
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-white border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap shadow-sm cursor-pointer"
            >
              <span>{typeFilter}</span>
              {typeCounts[typeFilter] !== undefined && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-indigo-50 text-indigo-600 font-bold">
                  {typeCounts[typeFilter]}
                </span>
              )}
              <i className={`ri-arrow-${typeDropdownOpen ? "up" : "down"}-s-line text-sm text-gray-400`} />
            </button>

            {typeDropdownOpen && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setTypeDropdownOpen(false)} />
                <div className="absolute right-0 top-full mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl z-30 py-2 w-56 max-h-72 overflow-y-auto">
                  {activityTypeFilters.map((f) => (
                    <button
                      key={f}
                      onClick={() => {
                        setTypeFilter(f);
                        setTypeDropdownOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-xs font-medium transition-colors whitespace-nowrap flex items-center justify-between cursor-pointer ${
                        typeFilter === f
                          ? "bg-indigo-50 text-indigo-600 font-bold"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <span>{f}</span>
                      {typeCounts[f] !== undefined && (
                        <span className="text-[11px] text-gray-400 font-normal">{typeCounts[f]}</span>
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Main Feed Card Container */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm relative" ref={feedRef}>
          {/* Feed Header bar */}
          <div className="flex items-center justify-between px-6 py-3.5 border-b border-gray-100 bg-white">
            <span className="text-xs font-bold text-gray-700">
              {filteredActivities.length} {filteredActivities.length === 1 ? "activity" : "activities"}
            </span>

            <div className="flex items-center gap-2">
              <span className="relative flex w-2 h-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full w-2 h-2 bg-green-500" />
              </span>
              <span className="text-xs font-medium text-green-700">Live · Updates every 30s</span>
            </div>
          </div>

          {/* Floating new activities pill */}
          {newActivityToast.visible && (
            <div className="absolute top-14 left-1/2 -translate-x-1/2 z-30 animate-bounce">
              <button
                onClick={handleLoadNew}
                className="flex items-center gap-2 bg-indigo-600 text-white rounded-full px-5 py-2 shadow-xl text-xs font-semibold hover:bg-indigo-700 transition-all cursor-pointer whitespace-nowrap ring-4 ring-indigo-100"
              >
                <i className="ri-arrow-up-line text-sm" />
                {newActivityToast.count} new activities — click to view
              </button>
            </div>
          )}

          {/* Feed Content */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-10 h-10 rounded-full border-3 border-indigo-200 border-t-indigo-600 animate-spin mb-3" />
              <p className="text-xs font-medium text-gray-500">Loading activities...</p>
            </div>
          ) : filteredActivities.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center px-4">
              <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center mb-3 text-indigo-500">
                <i className="ri-pulse-line text-2xl" />
              </div>
              <p className="text-sm font-bold text-gray-800 mb-1">No activities found for {dateLabel}</p>
              <p className="text-xs text-gray-500 max-w-sm">
                Perform any CRM actions (add leads, change stages, log calls, WhatsApp, quotations, project units) to automatically generate real-time activity feed!
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {timeBlocks.map((block) => {
                const items = groupedActivities[block];
                if (!items || items.length === 0) return null;
                return (
                  <div key={block}>
                    {/* Time block header */}
                    <div className="px-6 py-2.5 bg-gray-50/70 border-b border-gray-100">
                      <span className="text-[11px] uppercase font-bold text-gray-400 tracking-wider">
                        {block}
                      </span>
                    </div>

                    <div className="divide-y divide-gray-50">
                      {items.map((activity) => {
                        const isNew = newItemIds.has(activity.id);
                        const displayTime = formatTimeFromDate(activity.timestamp, activity.time);
                        return (
                          <div
                            key={activity.id}
                            className={`flex items-start justify-between gap-4 px-6 py-4 hover:bg-gray-50/70 transition-all ${
                              isNew ? "bg-green-50/60 border-l-4 border-l-green-500" : ""
                            }`}
                          >
                            <div className="flex items-start gap-3.5 flex-1 min-w-0">
                              {/* User Initials Avatar */}
                              <div className="w-9 h-9 rounded-full bg-indigo-50 text-indigo-700 font-bold text-xs flex items-center justify-center flex-shrink-0">
                                {activity.agentInitials || "SY"}
                              </div>

                              {/* Text info */}
                              <div className="flex-1 min-w-0">
                                <div className="text-sm text-gray-800 leading-snug">
                                  <span className="font-semibold text-gray-900">{activity.agent}</span>
                                  <span className="text-gray-500"> {activity.action} </span>
                                  {activity.leadName && (
                                    <button
                                      onClick={() => handleLeadClick(activity)}
                                      className="font-bold text-indigo-600 hover:text-indigo-800 hover:underline cursor-pointer inline"
                                    >
                                      {activity.leadName}
                                    </button>
                                  )}
                                </div>

                                {activity.detail && (
                                  <p
                                    onClick={() => handleActionDetailClick(activity)}
                                    className="text-xs text-gray-500 mt-1 cursor-pointer hover:text-gray-700 transition-colors leading-relaxed"
                                  >
                                    {activity.detail}
                                  </p>
                                )}
                              </div>
                            </div>

                            {/* Right side Icon & Time */}
                            <div className="flex flex-col items-end gap-1 flex-shrink-0 pt-0.5">
                              <button
                                onClick={() => handleActionDetailClick(activity)}
                                className={`w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer transition-transform hover:scale-105 ${getActivityIconBg(
                                  activity.type
                                )}`}
                                title={`Type: ${activity.type}`}
                              >
                                <i className={`${getActivityIcon(activity.type)} text-sm`} />
                              </button>
                              <span className="text-[11px] font-medium text-gray-400 whitespace-nowrap">
                                {displayTime}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Log Activity Modal */}
      {showLogModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-gray-200 overflow-hidden max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/60">
              <div>
                <h3 className="text-base font-bold text-gray-900">Log Team Activity</h3>
                <p className="text-xs text-gray-500">Record a call, meeting, note, or update into the timeline</p>
              </div>
              <button
                onClick={() => setShowLogModal(false)}
                className="w-8 h-8 rounded-lg hover:bg-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <form onSubmit={handleSaveActivityModal} className="p-6 space-y-4 overflow-y-auto flex-1">
              <div className="relative">
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Lead Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    required
                    placeholder="Search or enter lead name..."
                    value={logForm.leadName}
                    onChange={(e) => {
                      setLogForm((prev) => ({ ...prev, leadName: e.target.value, leadId: "" }));
                      setLeadSearchText(e.target.value);
                      setLeadDropdownOpen(true);
                    }}
                    onFocus={() => setLeadDropdownOpen(true)}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-all"
                  />
                </div>

                {leadDropdownOpen && filteredLeadsForModal.length > 0 && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-20 max-h-44 overflow-y-auto divide-y divide-gray-50">
                    {filteredLeadsForModal.map((lead) => (
                      <div
                        key={lead.id}
                        onClick={() => {
                          setLogForm((prev) => ({ ...prev, leadName: lead.name, leadId: String(lead.id) }));
                          setLeadDropdownOpen(false);
                        }}
                        className="px-4 py-2 hover:bg-indigo-50/50 cursor-pointer flex items-center justify-between transition-colors"
                      >
                        <span className="text-sm font-semibold text-gray-800">{lead.name}</span>
                        {lead.company && <span className="text-xs text-gray-400">{lead.company}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Activity Type</label>
                  <select
                    value={logForm.type}
                    onChange={(e) => setLogForm((prev) => ({ ...prev, type: e.target.value }))}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-all"
                  >
                    <option value="Call">Call</option>
                    <option value="Note">Note</option>
                    <option value="Meeting">Meeting</option>
                    <option value="WhatsApp">WhatsApp</option>
                    <option value="Stage Change">Stage Change</option>
                    <option value="Quotation">Quotation</option>
                    <option value="Task">Task</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Logged By (Agent)</label>
                  <select
                    value={logForm.agent}
                    onChange={(e) => setLogForm((prev) => ({ ...prev, agent: e.target.value }))}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-all"
                  >
                    {agentsList
                      .filter((a) => a.name !== "Everyone")
                      .map((a) => (
                        <option key={a.name} value={a.name}>
                          {a.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Date</label>
                  <input
                    type="date"
                    value={logForm.date}
                    onChange={(e) => setLogForm((prev) => ({ ...prev, date: e.target.value }))}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Time</label>
                  <input
                    type="time"
                    value={logForm.time}
                    onChange={(e) => setLogForm((prev) => ({ ...prev, time: e.target.value }))}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Activity Notes / Details <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  rows={3}
                  placeholder="Describe what was discussed, outcome, or follow-up details..."
                  value={logForm.detail}
                  onChange={(e) => setLogForm((prev) => ({ ...prev, detail: e.target.value }))}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-all resize-none"
                />
              </div>

              <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowLogModal(false)}
                  className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingLog}
                  className="px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-all shadow-md cursor-pointer disabled:opacity-50"
                >
                  {submittingLog ? "Saving..." : "Record Activity"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </CrmLayout>
  );
}